import { useCallback, useRef, useState } from "react";
import { ChatMessage, MessageRole, ToolStep } from "../types";
import { PARROT_CHAT_ENDPOINT, sendMessage } from "../services/chatService";
import { PARROT_CHAT_STREAM_ENDPOINT, streamMessage } from "../services/streamService";

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const STOPPED = "Stopped.";
const CONNECTION_ERROR = "Connection error — please try again.";
const NO_RESPONSE = "No response received.";

export type ResponseMode = "json" | "stream";

interface ExchangeComplete {
	userMessage: string;
	agentMessage: string;
	remoteSessionId?: string;
	rawResponse?: unknown;
}

interface UseChatArgs {
	responseMode: ResponseMode;
	agentType: string;
	authToken?: string;
	headers?: Record<string, string>;
	onUserMessage: (msg: ChatMessage) => void;
	onAssistantMessage: (msg: ChatMessage, remoteSessionId?: string) => void;
	/** Patches a message already added through `onAssistantMessage`; used while streaming. */
	onAssistantUpdate: (messageId: string, patch: Partial<ChatMessage>, remoteSessionId?: string) => void;
	/** Fired only on a successful exchange (both messages present), for persistence. */
	onExchangeComplete?: (exchange: ExchangeComplete) => void;
	getRemoteSessionId: () => string | undefined;
	/** Consumer fields merged into the request body. Read once per send, before anything else
	 *  happens, so it sees state as it was when the user hit send. */
	getRequestBody?: () => Record<string, unknown> | undefined;
}

const isAbort = (e: unknown) =>
	(e instanceof DOMException && e.name === "AbortError") ||
	(e instanceof Error && e.name === "AbortError");

const message = (role: MessageRole, content: string, extra?: Partial<ChatMessage>): ChatMessage => ({
	id: newId(),
	role,
	content,
	createdAt: new Date().toISOString(),
	...extra,
});

export const useChat = ({
	responseMode,
	agentType,
	authToken,
	headers,
	onUserMessage,
	onAssistantMessage,
	onAssistantUpdate,
	onExchangeComplete,
	getRemoteSessionId,
	getRequestBody,
}: UseChatArgs) => {
	const [isLoading, setIsLoading] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	const sendJson = useCallback(
		async (query: string, signal: AbortSignal, body?: Record<string, unknown>) => {
			try {
				const { content, sessionId, raw } = await sendMessage({
					endpoint: PARROT_CHAT_ENDPOINT,
					agentType,
					query,
					sessionId: getRemoteSessionId(),
					authToken,
					headers,
					signal,
					body,
				});

				const agentMessage = content || NO_RESPONSE;
				onAssistantMessage(message("assistant", agentMessage), sessionId);
				onExchangeComplete?.({
					userMessage: query,
					agentMessage,
					remoteSessionId: sessionId,
					rawResponse: raw,
				});
			} catch (e) {
				onAssistantMessage(
					signal.aborted || isAbort(e)
						? message("assistant", STOPPED)
						: message("assistant", CONNECTION_ERROR, { error: true })
				);
			}
		},
		[agentType, authToken, headers, onAssistantMessage, onExchangeComplete, getRemoteSessionId]
	);

	const sendStream = useCallback(
		async (query: string, signal: AbortSignal, body?: Record<string, unknown>) => {
			const placeholder = message("assistant", "", { steps: [], status: "streaming" });
			onAssistantMessage(placeholder);

			let content = "";
			let steps: ToolStep[] = [];
			let toolSinceText = false;

			// Coalesce ~one update per token into one per frame. A hidden tab pauses rAF; the
			// final flush on settle still lands everything.
			let frame: number | null = null;
			const scheduleFlush = () => {
				if (frame !== null) return;
				frame = requestAnimationFrame(() => {
					frame = null;
					onAssistantUpdate(placeholder.id, { content, steps });
				});
			};
			const settle = (patch: Partial<ChatMessage>, remoteSessionId?: string) => {
				if (frame !== null) cancelAnimationFrame(frame);
				frame = null;
				// createdAt moves to completion so the idle timeout counts from the reply's end,
				// matching the JSON path.
				onAssistantUpdate(
					placeholder.id,
					{ content, steps, status: "done", createdAt: new Date().toISOString(), ...patch },
					remoteSessionId
				);
			};

			try {
				const done = await streamMessage(
					{
						endpoint: PARROT_CHAT_STREAM_ENDPOINT,
						agentType,
						query,
						sessionId: getRemoteSessionId(),
						authToken,
						headers,
						signal,
						body,
					},
					(event) => {
						switch (event.type) {
							case "token":
								// Narration either side of a tool call arrives with no separator.
								if (toolSinceText && content) content += "\n\n";
								toolSinceText = false;
								content += event.text;
								break;
							case "tool_start":
								steps = [...steps, { id: event.id, name: event.name, status: "running" }];
								toolSinceText = true;
								break;
							case "tool_result":
								steps = steps.map((s) =>
									s.id === event.id
										? {
												...s,
												status: event.status === "error" ? "error" : "success",
												durationMs: event.durationMs,
											}
										: s
								);
								break;
						}
						scheduleFlush();
					}
				);

				// A tool with no tool_result by done never finished; don't leave it spinning.
				steps = steps.map((s) => (s.status === "running" ? { ...s, status: "cancelled" } : s));
				const agentMessage = content || NO_RESPONSE;
				settle({ content: agentMessage }, done.sessionId);
				onExchangeComplete?.({
					userMessage: query,
					agentMessage,
					remoteSessionId: done.sessionId,
					rawResponse: { done: done.raw, steps },
				});
			} catch (e) {
				steps = steps.map((s) => (s.status === "running" ? { ...s, status: "cancelled" } : s));
				// Some engines surface a cancelled body read as a plain network error, so trust the signal.
				if (signal.aborted || isAbort(e)) settle({ content: content || STOPPED });
				else settle({ error: true });
			}
		},
		[agentType, authToken, headers, onAssistantMessage, onAssistantUpdate, onExchangeComplete, getRemoteSessionId]
	);

	const send = useCallback(
		async (text: string) => {
			const trimmed = text.trim();
			if (!trimmed || isLoading) return;

			const body = getRequestBody?.();
			onUserMessage(message("user", trimmed));

			setIsLoading(true);
			abortRef.current?.abort();
			abortRef.current = new AbortController();

			try {
				const run = responseMode === "stream" ? sendStream : sendJson;
				await run(trimmed, abortRef.current.signal, body);
			} finally {
				setIsLoading(false);
				abortRef.current = null;
			}
		},
		[isLoading, responseMode, onUserMessage, sendJson, sendStream, getRequestBody]
	);

	const abort = useCallback(() => {
		abortRef.current?.abort();
	}, []);

	return { send, abort, isLoading };
};
